import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ownerActions, ownerSelectors } from "../../../ducks/owner";
import { AppDispatch } from "../../../store";
import {
  TableHeaderItem,
  TablePagination,
  TableSortBy,
} from "../../../hooks/useTable";
import { Owner } from "../../../ducks/owner/types";
import { ToastType } from "../../atoms/Toast";
import { OwnerCreateRequestBody } from "../../../core/api/requests/owner/create/types";
import { OwnerEditRequestBody } from "../../../core/api/requests/owner/edit/types";
import { appActions } from "../../../ducks/app";
import { ClickableText } from "../../atoms/ClickableText";
import { useModalWithProtection } from "../../../hooks/useModalWithProtection";

// Utility function to parse API error responses
const parseApiError = (error: any): string => {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  // Handle HTTP status codes first
  if (error.response?.status) {
    const status = error.response.status;
    if (status === 504) {
      return "Gateway timeout. The server took too long to respond. Please try again.";
    }
    if (status === 503) {
      return "Service unavailable. The server is temporarily unavailable. Please try again later.";
    }
    if (status >= 500) {
      return "Server error. Please try again later or contact support.";
    }
  }

  // Handle timeout errors (ECONNABORTED or timeout messages)
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return "Request timeout. The server took too long to respond. Please try again.";
  }

  // Handle different error response formats
  if (error.response?.data) {
    const data = error.response.data;

    // Handle validation errors (Django REST framework format)
    if (typeof data === 'object') {
      const errorMessages: string[] = [];

      // Extract error messages from nested objects
      const extractErrors = (obj: any) => {
        for (const [key, value] of Object.entries(obj)) {
          if (Array.isArray(value)) {
            // Handle array of error messages
            value.forEach((msg: string) => {
              errorMessages.push(msg);
            });
          } else if (typeof value === 'object' && value !== null) {
            // Recursively handle nested objects
            extractErrors(value);
          } else if (typeof value === 'string') {
            // Handle direct string error messages
            errorMessages.push(value);
          }
        }
      };

      extractErrors(data);

      if (errorMessages.length > 0) {
        return errorMessages.join('\n');
      }
    }

    // Handle direct string error messages
    if (typeof data === 'string') {
      return data;
    }

    // Handle error message field
    if (data.message) {
      return data.message;
    }

    // Handle error detail field
    if (data.detail) {
      return data.detail;
    }
  }

  // Handle network errors - but don't show misleading message for timeouts
  // Timeouts will be handled specially in the delete handler
  if (error.message && !error.message.includes('timeout') && error.code !== 'ECONNABORTED') {
    if (error.message.includes('Network Error')) {
      // Only show network error if it's not a timeout
      return "Network error. Please check your connection and try again.";
    }
    return error.message;
  }

  // Fallback error message
  return "An unexpected error occurred. Please try again.";
};

export const useOwnerPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const owners = useSelector(ownerSelectors.owners) ?? [];
  const pageSize = useSelector(ownerSelectors.pageSize);
  const currentPage = useSelector(ownerSelectors.currentPage);
  const totalItems = useSelector(ownerSelectors.totalItems);
  const totalPages = useSelector(ownerSelectors.totalPages);
  const pendingDeleteIdRef = useRef<string | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial data fetch
  useEffect(() => {
    dispatch(ownerActions.fetchOwners.started({
      page: currentPage,
      page_size: pageSize,
      name: debouncedSearchTerm || undefined,
      onComplete: undefined,
      onError: undefined,
      onSuccess: undefined,
    }));
  }, [dispatch, pageSize]); // Only run on mount and pageSize change

  // Check if pending deletion was successful after refetch
  useEffect(() => {
    if (pendingDeleteIdRef.current && owners) {
      const wasDeleted = !owners.some(owner => owner.id === pendingDeleteIdRef.current);
      if (wasDeleted) {
        // Deletion was successful despite timeout
        pendingDeleteIdRef.current = null;
        // Don't show error - just silently succeed since deletion worked
      } else {
        // Deletion failed - item still exists
        const deleteId = pendingDeleteIdRef.current;
        pendingDeleteIdRef.current = null;
        dispatch(
          appActions.showModal({
            title: "Cannot Delete Owner",
            subTitle: "The deletion may not have completed. Please try again.",
            showCloseButton: false,
            buttons: [
              {
                text: "OK",
                variant: "filled",
                onClick: () => dispatch(appActions.hideModal()),
              },
            ],
          })
        );
      }
    }
  }, [owners, dispatch]);

  const ownersWithNo = owners.map((item, idx) => ({
    ...item,
    no: (currentPage - 1) * pageSize + idx + 1,
  }));

  const fetchDataFunction = useCallback((sort: TableSortBy, pagination?: TablePagination) => {
    const fetchPage = pagination?.page ?? currentPage;
    const fetchPageSize = pagination?.size ?? pageSize;
    dispatch(ownerActions.fetchOwners.started({
      page: fetchPage,
      page_size: fetchPageSize,
      name: debouncedSearchTerm || undefined,
      onComplete: undefined,
      onError: undefined,
      onSuccess: undefined,
    }));
  }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

  // Modal logic
  const { isOpen: isCreateModalOpen, openModal: handleOpenCreateModal, closeModal: handleCloseCreateModal } = useModalWithProtection();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const handleOpenEditModal = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedOwner(null);
  };
  const handleOpenDetailModal = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsDetailModalOpen(true);
  };
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedOwner(null);
  };

  const tableHeaders: TableHeaderItem<Owner & { no: number }>[] = useMemo(
    () => [
      { id: 1, title: "No", dataKey: "no" },
      {
        id: 2,
        title: "Name",
        dataKey: "name",
        popupTitle: "Name",
        dataMapFunction: (owner) => ({
          text: owner.name,
          onClick: () => handleOpenDetailModal(owner),
        }),
        customComponent: ClickableText,
      },
      { id: 3, title: "Email", dataKey: "email", popupTitle: "Email" },
      {
        id: 4,
        title: "Phone Number",
        dataKey: "phone_number",
        popupTitle: "Phone",
      },
    ],
    [handleOpenDetailModal]
  );

  // Add a handler to change page and fetch data
  const handlePageChange = (newPage: number) => {
    // Update currentPage in Redux state - useEffect will handle the API call
    dispatch(ownerActions.fetchOwners.started({
      page: newPage,
      page_size: pageSize,
      name: debouncedSearchTerm || undefined,
      onComplete: undefined,
      onError: undefined,
      onSuccess: undefined,
    }));
  };

  const handleRowDelete = useCallback(
    (data: Owner) => {
      dispatch(
        appActions.showModal({
          title: "Delete Confirmation",
          subTitle: `Are you sure you want to delete "${data.name}"?`,
          buttons: [
            {
              text: "Cancel",
              variant: "border",
              onClick: () => dispatch(appActions.hideModal()),
            },
            {
              text: "Delete",
              variant: "filled",
              onClick: () => {
                dispatch(appActions.hideModal());
                dispatch(appActions.showLoading());
                dispatch(
                  ownerActions.deleteOwner.started({
                    id: data.id,
                    onComplete: () => {
                      dispatch(appActions.hideLoading());
                    },
                    onSuccess: () => {
                      // Refetch owners on successful deletion
                      dispatch(
                        ownerActions.fetchOwners.started({
                          page: currentPage,
                          page_size: pageSize,
                          name: debouncedSearchTerm || undefined,
                        })
                      );
                      // Show success message
                      // dispatch(
                      //   appActions.showModal({
                      //     title: "Success",
                      //     subTitle: `Owner "${data.name}" has been deleted successfully.`,
                      //     buttons: [
                      //       {
                      //         text: "OK",
                      //         variant: "filled",
                      //         onClick: () => dispatch(appActions.hideModal()),
                      //       },
                      //     ],
                      //   })
                      // );
                    },
                    onError: (error: any) => {
                      // Check if this is a timeout/network error (no response status)
                      // If deletion times out, refetch to verify if it actually succeeded
                      const isTimeout = !error.response?.status && (
                        error.code === 'ECONNABORTED' ||
                        error.message?.includes('timeout') ||
                        error.message?.includes('Network Error')
                      );

                      if (isTimeout) {
                        // Store the ID we're trying to delete
                        pendingDeleteIdRef.current = data.id;
                        // Refetch owners to check if deletion actually succeeded
                        dispatch(
                          ownerActions.fetchOwners.started({
                            page: currentPage,
                            page_size: pageSize,
                            name: debouncedSearchTerm || undefined,
                          })
                        );
                        // Don't show error yet - wait for refetch to complete
                        return;
                      }

                      // For other errors, show the error message
                      const errorMessage = parseApiError(error);
                      dispatch(
                        appActions.showModal({
                          title: "Cannot Delete Owner",
                          subTitle: errorMessage,
                          showCloseButton: false,
                          buttons: [
                            {
                              text: "OK",
                              variant: "filled",
                              onClick: () => dispatch(appActions.hideModal()),
                            },
                          ],
                        })
                      );
                    },
                  })
                );
              },
            },
          ],
        })
      );
    },
    [dispatch, currentPage, pageSize, debouncedSearchTerm]
  );

  // Search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      dispatch(ownerActions.fetchOwners.started({
        page: 1,
        page_size: pageSize,
        name: debouncedSearchTerm || undefined,
        onComplete: undefined,
        onError: undefined,
        onSuccess: undefined,
      }));
    }
  }, [debouncedSearchTerm]);

  // Only show pagination if there's more than one page
  const hasPagination = totalPages > 1;



  return {
    tableHeaders,
    owners: ownersWithNo,
    fetchDataFunction,
    isCreateModalOpen,
    isEditModalOpen,
    isDetailModalOpen,
    selectedOwner,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleOpenEditModal,
    handleCloseEditModal,
    handleOpenDetailModal,
    handleCloseDetailModal,
    page: currentPage,
    pageSize,
    totalItems,
    handleRowDelete,
    hasPagination,
    searchTerm,
    handleSearchChange,
    refetchOwners: () =>
      fetchDataFunction(
        { field: "name", order: "asc" },
        { page: currentPage, size: pageSize }
      ),
    handlePageChange, // export the handler
  };
};

export const useOwnerToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    visible: boolean;
  }>({ message: "", type: "success", visible: false });

  const showToast = (message: string, type: ToastType = "success") => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 2000);
  };

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  return { toast, showToast, hideToast };
};

export const useOwnerRegistration = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchOwners?: () => void,
  closeModal?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittingRef = useRef(false);

  const handleRegisterOwner = useCallback(
    (
      data: OwnerCreateRequestBody,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;

      dispatch(
        ownerActions.createNewOwner.started({
          ...data,
          onComplete: () => {
            isSubmittingRef.current = false;
          },
          onSuccess: (result: any) => {
            isSubmittingRef.current = false;
            showToast("Owner created successfully!", "success");
            setTimeout(() => {
              if (refetchOwners) refetchOwners();
              if (closeModal) closeModal();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (err: any) => {
            isSubmittingRef.current = false;
            const errorMessage = parseApiError(err);
            showToast(errorMessage, "error");
            if (onError) onError(errorMessage);
          },
        })
      );
    },
    [dispatch, showToast, refetchOwners, closeModal]
  );

  return { handleRegisterOwner };
};

export const useOwnerEditing = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchOwners?: () => void,
  closeModal?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const isSubmittingRef = useRef(false);

  const handleEditOwner = useCallback(
    (
      data: OwnerEditRequestBody,
      id: string,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;

      return dispatch(
        ownerActions.updateOwner.started({
          ...data,
          id,
          onComplete: () => {
            isSubmittingRef.current = false;
          },
          onSuccess: (result: any) => {
            isSubmittingRef.current = false;
            showToast("Owner updated succesfully!", "success");
            setTimeout(() => {
              if (refetchOwners) refetchOwners();
              if (closeModal) closeModal();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (err: any) => {
            isSubmittingRef.current = false;
            const errorMessage = parseApiError(err);
            showToast(errorMessage, "error");
            if (onError) onError(errorMessage);
          },
        })
      );
    },
    [dispatch, showToast, refetchOwners, closeModal]
  );

  return { handleEditOwner };
};
